import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import styles from '../../views/ProductDetails/ProductDetails.module.scss';
import ultraStyles from './UltraPDP.module.scss';
import HideChatWidget from '../ZendeskChat/HideChatWidget';
import AppPaths from '../../AppPaths';
import Footer from '../Footer/Footer';
import NavBar from '../AppBar/NavBar';
import PlusMembershipValueProps from '../../views/ProductDetails/Series3CollarDetails/components/PlusMembershipValueProps';
import FAQ from '../../views/ProductDetails/Series3CollarDetails/components/FAQ';
import CollarAddToBagButton from '../../views/ProductDetails/Series3CollarDetails/components/CollarAddToBagButton';
import * as types from '../../types';
import { cartActions } from '../../reducers/storeCart';
import useSubscriptionProducts from '../../hooks/useSubscriptionProducts';
import { useLocale } from '../../contexts/LocaleContext';
import { CustomerIO } from '../../lib/analytics/customerIO';
import { formattedCentsToDollars } from '../../lib/util';

import { ProductTitle, ProductTitleDescription } from './ProductTitle/ProductTitle';
import UltraMembershipOptions from './UltraMembershipOptions/UltraMembershipOptions';
import UltraProductSummary from './UltraProductSummary/UltraProductSummary';
import UltraAppCarousel from './UltraAppCarousel/UltraAppCarousel';
import UltraAllFeatures from './UltraAllFeatures/UltraAllFeatures';
import UltraDesktopImageGallery from './UltraDesktopImageGallery/UltraDesktopImageGallery';
import useUltraExperienceGuard from './useUltraExperienceGuard';
import { usePdpImagesContext } from '../../contexts/PdpImagesContext';

export default function UltraPDP() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const products = useSelector((state: types.AppState) => state.config.products);
  const { currency } = useLocale();

  // Get pre-loaded dynamic images from context
  const { getMedia } = usePdpImagesContext();
  const dynamicImages = getMedia(types.ultraCollarId);

  const product = products.find((p) => p.id === types.ultraCollarId) as (types.IProduct & { sku: string }) | undefined;

  const allUltraSubscriptions = useSubscriptionProducts(types.ultraCollarId);
  const subscriptions = allUltraSubscriptions.filter((p) => !p.thirdParty);

  const initialSubscription = subscriptions.find((s) => s.renewalMonths === 12);
  const [selectedSubscription, setSelectedSubscription] = useState<types.ISubscriptionProduct | undefined>(
    initialSubscription,
  );

  useEffect(() => {
    if (!selectedSubscription && subscriptions.length > 0) {
      const oneYear = subscriptions.find((s) => s.renewalMonths === 12);
      setSelectedSubscription(oneYear ?? subscriptions[0]);
    }
  }, [subscriptions, selectedSubscription]);

  const productSku = product?.sku ?? 'U1-MU';

  const addToBag = useCallback(() => {
    if (!selectedSubscription) {
      return false;
    }

    dispatch(
      cartActions.addBundleCartItem({
        sku: productSku,
        subscriptionSku: selectedSubscription.sku,
        addonSkus: [],
        currency,
        isGift: false,
      }),
    );

    CustomerIO.trackAddedItemToCart(types.ultraCollarId);
    history.push(AppPaths.Bag);
    return true;
  }, [dispatch, productSku, selectedSubscription, currency, history]);

  const productPriceInCents = product!.pricingInCents.USD;

  const descriptionText = selectedSubscription
    ? t('ultra_cart_description', {
        hardwarePrice: `${formattedCentsToDollars(productPriceInCents)}`,
        membershipPrice: `${formattedCentsToDollars(selectedSubscription.pricingInCents.USD)}`,
      })
    : null;

  const isUltraExperienceEnabled = useUltraExperienceGuard();

  if (!isUltraExperienceEnabled) {
    return null;
  }

  return (
    <>
      <div
        className={classNames(styles.productContainer, styles.ecomFlowRevamp, 'ecom-flow-revamp', 'ultra-pdp', {
          'ecom-flow-revamp-light-v2': true,
        })}
      >
        <NavBar />
        <div className={classNames('image-gallery-container', styles.galleryContainer)}>
          <UltraDesktopImageGallery dynamicImages={dynamicImages} />
        </div>
        <div className={classNames(styles.productDetails, 'product-details')}>
          <div className={styles.productTitleContainer}>
            <ProductTitle />
            <ProductTitleDescription />
            <UltraMembershipOptions
              subscriptions={subscriptions}
              selectedSubscription={selectedSubscription}
              onSelectSubscription={setSelectedSubscription}
            />
          </div>
          <PlusMembershipValueProps />
          <CollarAddToBagButton
            ctaText={selectedSubscription ? t('add_to_cart') : t('ultra_pick_your_membership')}
            descriptionText={descriptionText}
            isVisibleOverride={!!selectedSubscription}
            onAddToBag={addToBag}
          />
        </div>
      </div>
      <UltraProductSummary />
      <UltraAppCarousel />
      <UltraAllFeatures />
      <FAQ />
      <div className={ultraStyles.footerContainer}>
        <Footer />
      </div>
      <HideChatWidget />
    </>
  );
}
